import React, { useEffect, useState } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { Bounce, toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../OnboardingComponents/CustomButton';
import CustomIconButton from '../OnboardingComponents/CustomIconButton';
import Pagination from '../Listing/Pagination';
import SearchInput from '../Listing/SearchInput';
import FilterDropdown from '../Listing/FilterDropdown';
import EmptyState from '../Common/EmptyState';
import DeleteConfirmation from '../Common/DeleteConfirmation';
import TruncatedText from '../Common/TruncatedText';
import AcceptInviteModal from '../Modals/AcceptInviteModal';
import { Avatar } from './AvatarGroup';
import { ICONS } from '../../constants/icons';
import { del, get, post } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { toastApiError } from '../../helpers/apiError';
import { findProjectMessageContextId } from '../../helpers/projectMessageContext';
import { nameWithYou } from '../../helpers/userDisplay';

const SKELETON_ROWS = 8;
const TABLE_COLUMNS = 7;

const DEFAULT_SORT_BY = 'created_at';
const DEFAULT_SORT_DIRECTION = 'desc';

const COLLAB_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const COLLAB_SORT_OPTIONS = [
  { value: 'created_at', label: 'Created At' },
  { value: 'status', label: 'Status' },
  { value: 'role', label: 'Role' },
];

const STATUS_CLASS = {
  Pending: 'pending',
  Rejected: 'rejected',
  Active: 'active',
  Withdrawn: 'withdrawn',
  pending: 'pending',
  rejected: 'rejected',
  active: 'active',
  withdrawn: 'withdrawn',
};

const formatStatusLabel = (status) => {
  if (!status) return '—';
  const normalized = String(status);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
};

const requestAdditionalSchema = Yup.object({
  limit: Yup.number()
    .typeError('Enter a valid number of collaborators')
    .integer('Enter a whole number')
    .min(1, 'Request at least 1 additional collaborator')
    .required('Please enter how many collaborators you need'),
  reason: Yup.string()
    .trim()
    .min(10, 'Reason must be at least 10 characters')
    .max(1000, 'Reason must be 1000 characters or less')
    .required('Please provide a reason'),
});

const RequestAdditionalModal = ({ isOpen, onClose, projectId }) => {
  const formik = useFormik({
    initialValues: {
      limit: '',
      reason: '',
    },
    validationSchema: requestAdditionalSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!projectId) {
        toast.error('Project is missing. Unable to submit request.', {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        });
        setSubmitting(false);
        return;
      }

      try {
        const response = await post(
          endpoints.requestProjectMemberLimit(projectId),
          {
            limit: Number(values.limit),
            reason: values.reason.trim(),
          }
        );

        // axios already toasts API failures and returns null — do not also success-toast.
        if (!response?.data?.success) {
          toastApiError(response, 'Failed to submit request')
          return
        }

        toast.success(
          response?.data?.message || 'Limit increase request submitted.',
          {
            position: 'bottom-center',
            theme: 'colored',
            transition: Bounce,
          }
        );
        resetForm();
        onClose();
      } catch (err) {
        toastApiError(err, 'Failed to submit request');
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (isOpen) return undefined;
    formik.resetForm();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    onClose();
  };

  const limitError =
    formik.touched.limit && formik.errors.limit ? formik.errors.limit : null;
  const reasonError =
    formik.touched.reason && formik.errors.reason ? formik.errors.reason : null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      className="amok-modal amok-modal--shadow-session amok-modal--collab-request"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body project-collab-modal__body">
        <form
          className="project-collab-modal project-collab-modal--request"
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <div className="project-collab-modal__main">
            <div className="project-collab-modal__header">
              <h3 className="project-collab-modal__title">
                Request Additional Collaborators
              </h3>
              <p className="project-collab-modal__subtitle">
                Fill in the details below to request a limit increase from the
                City Editor.
              </p>
            </div>

            <div className="project-collab-modal__fields">
              <div className="project-collab-modal__field">
                <label
                  className="project-collab-modal__label"
                  htmlFor="collab-request-slots"
                >
                  How many additional collaborators do you need?
                </label>
                <input
                  id="collab-request-slots"
                  name="limit"
                  type="number"
                  min={1}
                  step={1}
                  className="project-collab-modal__input"
                  value={formik.values.limit}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="e.g: 5"
                  aria-invalid={limitError ? 'true' : undefined}
                />
                {limitError ? (
                  <span className="project-collab-modal__error">{limitError}</span>
                ) : null}
              </div>

              <div className="project-collab-modal__field">
                <label
                  className="project-collab-modal__label"
                  htmlFor="collab-request-reason"
                >
                  Reason & Justification
                </label>
                <textarea
                  id="collab-request-reason"
                  name="reason"
                  className="project-collab-modal__note"
                  value={formik.values.reason}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Describe the reason here..."
                  rows={3}
                  maxLength={1000}
                  aria-invalid={reasonError ? 'true' : undefined}
                />
                <div className="project-collab-modal__note-meta">
                  {reasonError ? (
                    <span className="project-collab-modal__error">
                      {reasonError}
                    </span>
                  ) : (
                    <span />
                  )}
                  <span className="project-collab-modal__char-count">
                    {formik.values.reason.length}/1000
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="project-collab-modal__actions">
            <CustomButton
              variant="danger"
              size="md"
              type="button"
              onClick={handleClose}
              disabled={formik.isSubmitting}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              size="md"
              type="submit"
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting}
            >
              Submit Request
            </CustomButton>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

/**
 * ProjectCollaborators
 * ------------------------------------------------------------------
 * Collaborators tab for the project detail page. Status bar + table
 * with skeleton loading and server-driven pagination.
 * Invite / Request actions live in ProjectLayout tabs-actions.
 */
const ProjectCollaborators = ({
  projectId,
  collaborators = [],
  isLoading = false,
  error = null,
  currentPage = 1,
  totalItems = 0,
  pageSize = 10,
  onPageChange,
  onMemberRemoved,
  onMemberUpdated,
  currentLimit = 20,
  allocatedSlots,
  collabModal = null,
  setCollabModal,
  searchQuery = '',
  onSearchChange,
  statusFilters = "",
  onStatusFiltersChange,
  sortBy = DEFAULT_SORT_BY,
  onSortByChange,
  sortDirection = DEFAULT_SORT_DIRECTION,
  onSortDirectionChange,
  canManageActions = true,
}) => {
  const [deletingMember, setDeletingMember] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [messagingUserId, setMessagingUserId] = useState(null);
  const [collabStats, setCollabStats] = useState(null);
  const navigate = useNavigate();
  const currentUserId = useSelector((state) => state.auth?.currentUser?.id);
  const close = () => setCollabModal?.(null);
  const closeEditModal = () => setEditingMember(null);

  useEffect(() => {
    if (!projectId) return undefined;

    let cancelled = false;
    const loadStats = async () => {
      try {
        const response = await get(endpoints.getProjectCollaboratorStats(projectId));
        if (cancelled) return;
        if (response?.data?.success) {
          setCollabStats(response.data.result || null);
        } else {
          throw new Error(
            response?.data?.message || 'Failed to load collaborator stats'
          );
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, 'Failed to load collaborator stats');
        }
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleMessageCollaborator = async collaborator => {
    const userId = collaborator?.userId;
    if (!projectId || !userId || messagingUserId) return;

    setMessagingUserId(String(userId));
    try {
      const existingContextId = await findProjectMessageContextId(
        projectId,
        userId,
        currentUserId,
      );

      if (existingContextId) {
        navigate(`../messages/${existingContextId}`, { relative: 'path' });
        return;
      }

      navigate('../messages', {
        relative: 'path',
        state: {
          openNewMessage: true,
          messageUserId: String(userId),
        },
      });
    } finally {
      setMessagingUserId(null);
    }
  };

  const allocatedFallback = allocatedSlots ?? totalItems;
  const displayCurrentLimit = collabStats?.project_collaborator_limit ?? currentLimit;
  const displayActiveMembers = collabStats?.total_active_members ?? allocatedFallback;
  const atLimit = displayActiveMembers >= displayCurrentLimit;

  const openDeleteModal = (member) => {
    setDeletingMember(member);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setDeletingMember(null);
  };

  const handleDeleteConfirm = async () => {
    const inviteId = deletingMember?.id || deletingMember?.memberId;
    if (!inviteId) return;

    setIsDeleting(true);
    try {
      const response = await del(endpoints.deleteProjectInvitation(inviteId));

      if (!response || response?.data?.success === false) {
        if (response) {
          toast.error(
            response?.data?.message || 'Failed to remove collaborator',
            {
              position: 'bottom-center',
              theme: 'colored',
              transition: Bounce,
            }
          );
        }
        return;
      }

      toast.success(
        response?.data?.message ||
          `${deletingMember?.name || 'Collaborator'} removed successfully.`,
        {
          position: 'bottom-center',
          theme: 'colored',
          transition: Bounce,
        }
      );
      await onMemberRemoved?.();
    } catch (err) {
      toastApiError(err, 'Failed to remove collaborator');
    } finally {
      setIsDeleting(false);
      setDeletingMember(null);
    }
  };

  return (
    <div className="project-collaborators">
      <div className="project-collaborators__status-bar">
        <div className="project-collaborators__status">
          <h3 className="project-collaborators__status-title">Collaborators Status</h3>
          {atLimit && (
            <p className="project-collaborators__status-note">
              <span className="project-collaborators__status-dot" aria-hidden="true" />
              You have hit the allocation limit for your tier.
            </p>
          )}
        </div>

        <div className="project-collaborators__limit">
          <div className="project-collaborators__limit-item">
            <span className="project-collaborators__limit-label">Current Limit</span>
            <span className="project-collaborators__limit-value">
              {displayCurrentLimit} Members
            </span>
          </div>
          <div className="project-collaborators__limit-item">
            <span className="project-collaborators__limit-label">Allocated Slots</span>
            <span className="project-collaborators__limit-value">
              {displayActiveMembers} Members
            </span>
          </div>
        </div>
      </div>

      <div className="project-collaborators__controls">
        <SearchInput
          value={searchQuery}
          onChange={(e) => onSearchChange?.(e.target.value)}
          placeholder="Search collaborators..."
          iconSrc={ICONS.search}
        />
        <FilterDropdown
          filters={[
            {
              key: 'status',
              type: 'select',
              label: 'Status',
              multiple: false,
              value: Array.isArray(statusFilters)
                ? String(statusFilters[0] || "")
                : statusFilters || "",
              defaultValue: "",
              onChange: (next) => {
                const value = Array.isArray(next)
                  ? String(next[0] || "")
                  : String(next || "")
                onStatusFiltersChange?.(value)
              },
              options: COLLAB_STATUS_OPTIONS,
            },
            {
              key: 'sortBy',
              type: 'sortBy',
              label: 'Sort By',
              value: sortBy,
              defaultValue: DEFAULT_SORT_BY,
              onChange: (next) => onSortByChange?.(next),
              options: COLLAB_SORT_OPTIONS,
            },
            {
              key: 'sortDirection',
              type: 'sortDirection',
              label: 'Order',
              value: sortDirection,
              defaultValue: DEFAULT_SORT_DIRECTION,
              onChange: (next) => onSortDirectionChange?.(next),
            },
          ]}
        />
      </div>

      <div className="project-collaborators__table-wrap">
        <div
          className="project-collaborators__table"
          role="table"
          aria-busy={isLoading || undefined}
        >
          <div className="project-collaborators__head" role="row">
            <div role="columnheader">Collaborator Name</div>
            <div role="columnheader">City</div>
            <div role="columnheader">Role</div>
            <div role="columnheader">Role Description</div>
            <div role="columnheader">Added on</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Action</div>
          </div>

          {isLoading &&
            Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="project-collaborators__row project-collaborators__row--skeleton"
                role="row"
                aria-hidden="true"
              >
                {Array.from({ length: TABLE_COLUMNS }).map((__, cellIdx) => (
                  <div key={cellIdx} className="project-collaborators__skeleton-cell">
                    {cellIdx === 0 ? (
                      <div className="project-collaborators__skeleton-name">
                        <div className="project-collaborators__skeleton-avatar" />
                        <div className="project-collaborators__skeleton-bar" />
                      </div>
                    ) : (
                      <div className="project-collaborators__skeleton-bar" />
                    )}
                  </div>
                ))}
              </div>
            ))}

          {!isLoading &&
            collaborators.map((c, i) => {
              const statusLabel = formatStatusLabel(c.status);
              const statusClass = STATUS_CLASS[c.status] || STATUS_CLASS[statusLabel];
              const isWithdrawn =
                String(c.status || '').toLowerCase() === 'withdrawn';
              const isActive =
                String(c.status || '').toLowerCase() === 'active';
              const isCurrentUser =
                currentUserId != null &&
                c.userId != null &&
                String(c.userId) === String(currentUserId);

              return (
                <div
                  key={c.id || `${c.name}-${c.role}-${i}`}
                  className="project-collaborators__row"
                  role="row"
                >
                  <div role="cell" className="project-collaborators__name" data-label="Name">
                    <Avatar
                      src={c.avatar}
                      name={c.name}
                      size="sm"
                      className="project-collaborators__avatar"
                    />
                    <TruncatedText>
                      {nameWithYou(c.name, isCurrentUser)}
                    </TruncatedText>
                  </div>
                  <div role="cell" className="project-collaborators__cell" data-label="City">
                    <TruncatedText className="project-collaborators__cell-text">
                      {c.city || '—'}
                    </TruncatedText>
                  </div>
                  <div role="cell" className="project-collaborators__cell" data-label="Role">
                    <TruncatedText className="project-collaborators__cell-text">
                      {c.role || '—'}
                    </TruncatedText>
                  </div>
                  <div
                    role="cell"
                    className="project-collaborators__cell project-collaborators__cell--description"
                    data-label="Role Description"
                  >
                    <TruncatedText className="project-collaborators__cell-text">
                      {c.roleDescription || '—'}
                    </TruncatedText>
                  </div>
                  <div role="cell" className="project-collaborators__cell" data-label="Added on">
                    <TruncatedText className="project-collaborators__cell-text">
                      {c.addedOn || '—'}
                    </TruncatedText>
                  </div>
                  <div
                    role="cell"
                    className="project-collaborators__cell project-collaborators__cell--status"
                    data-label="Status"
                  >
                    <span
                      className={`project-collaborators__badge project-collaborators__badge--${
                        statusClass || 'pending'
                      }`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  <div
                    role="cell"
                    className="project-collaborators__actions-cell"
                    data-label="Action"
                  >
                    {canManageActions && !isWithdrawn ? (
                      <>
                        
                        {!isCurrentUser && c.userId && isActive ? (
                          <CustomIconButton
                            iconSrc={ICONS.messageTextBlue}
                            label={`Message ${c.name}`}
                            size="sm"
                            className="project-collaborators__action project-collaborators__action--message"
                            onClick={() => handleMessageCollaborator(c)}
                            disabled={Boolean(messagingUserId)}
                          />
                        ) : null}
                        {isActive ? (
                          <CustomIconButton
                            iconSrc={ICONS.edit}
                            label={`Edit ${c.name}`}
                            size="sm"
                            className="project-collaborators__action project-collaborators__action--edit"
                            onClick={() =>
                              setEditingMember({
                                ...c,
                                projectId,
                              })
                            }
                            disabled={!isCurrentUser}
                          />
                        ) : null}
                        <CustomIconButton
                          iconSrc={ICONS.trash}
                          label={`Remove ${c.name}`}
                          tone="danger"
                          size="sm"
                          className="project-collaborators__action project-collaborators__action--remove"
                          onClick={() => openDeleteModal(c)}
                          disabled={isDeleting}
                        />
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}

          {!isLoading && error && collaborators.length === 0 && (
            <div className="project-collaborators__empty project-collaborators__empty--error">
              {error}
            </div>
          )}

          {!isLoading && !error && totalItems === 0 && (
            <EmptyState
              title="No Collaborators Yet"
              description="Invite collaborators to start working together on this project."
            />
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={onPageChange}
        itemLabel="Collaborators"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="project-collaborators__pagination"
      />

      <DeleteConfirmation
        isOpen={Boolean(deletingMember)}
        onCancel={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Remove Collaborator"
        message={`Are you sure you want to remove ${
          deletingMember?.name || 'this collaborator'
        } from the project? This action cannot be undone.`}
        cancelText="Cancel"
        confirmText="Remove Collaborator"
        isDeleting={isDeleting}
      />

      <AcceptInviteModal
        isOpen={Boolean(editingMember)}
        onClose={closeEditModal}
        invite={editingMember}
        mode="edit"
        onSuccess={async () => {
          if (typeof onMemberUpdated === 'function') {
            await onMemberUpdated();
            return;
          }
          if (typeof onMemberRemoved === 'function') {
            await onMemberRemoved();
          }
        }}
      />

      <RequestAdditionalModal
        isOpen={collabModal === 'request'}
        onClose={close}
        projectId={projectId}
      />
    </div>
  );
};

export default ProjectCollaborators;
